import { createFileRoute, redirect } from '@tanstack/react-router'
import { Archive, Pencil, Plus, RotateCcw, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  useArchiveLocation,
  useEmployees,
  useManagedLocations,
  useRestoreLocation,
} from '@/api/queries'
import type { LocationAdminItem } from '@/api/types'
import { ConfirmDialog, Modal } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  cn,
} from '@/components/ui/primitives'
import { EmployeeAssignmentDialog } from '@/features/locations/employee-assignment'
import { LocationFormDialog, type LocationDialogState } from '@/features/locations/location-form'
import { apiErrorLabel } from '@/shared/labels'
import { canManageLocations } from '@/shared/permissions'

export const Route = createFileRoute('/_auth/locations/')({
  beforeLoad: ({ context }) => {
    if (!canManageLocations(context.me)) throw redirect({ to: '/' })
  },
  component: LocationsPage,
})

function LocationsPage() {
  const navigate = Route.useNavigate()
  const locations = useManagedLocations()
  const [form, setForm] = useState<LocationDialogState | null>(null)
  const [assignment, setAssignment] = useState<LocationAdminItem | null>(null)
  const [archiveTarget, setArchiveTarget] = useState<LocationAdminItem | null>(null)
  const employees = useEmployees(Boolean(assignment), true)
  const archive = useArchiveLocation()
  const restore = useRestoreLocation()

  const onError = (error: unknown) =>
    toast.error(
      apiErrorLabel((error as { detail?: unknown }).detail, 'Не удалось выполнить действие'),
    )

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Торговые точки</h1>
          <p className="mt-0.5 text-[13px] text-ink-subtle">
            Управление магазинами и назначениями сотрудников.
          </p>
        </div>
        <Button variant="primary" onClick={() => setForm({ mode: 'create' })}>
          <Plus className="size-4" />
          Добавить точку
        </Button>
      </header>

      <Card>
        {locations.isError ? (
          <ErrorState
            message={apiErrorLabel(
              (locations.error as { detail?: unknown }).detail,
              'Не удалось загрузить точки',
            )}
            onRetry={() => void locations.refetch()}
          />
        ) : locations.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-11" />
            ))}
          </div>
        ) : (locations.data?.length ?? 0) === 0 ? (
          <EmptyState
            title="Точек пока нет"
            description="Создайте первую торговую точку и назначьте ей сотрудников."
            action={
              <Button
                variant="primary"
                size="sm"
                className="mt-2"
                onClick={() => setForm({ mode: 'create' })}
              >
                Создать точку
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[13px] text-ink-subtle">
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Название
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Город
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Сотрудники
                  </th>
                  <th scope="col" className="px-5 py-2.5 font-medium">
                    Статус
                  </th>
                  <th scope="col" className="px-5 py-2.5 text-right font-medium">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {locations.data?.map((location) => (
                  <tr
                    key={location.id}
                    className={cn(
                      'border-b border-line/60 transition-colors last:border-0 hover:bg-surface-muted/60',
                      !location.is_active && 'opacity-60',
                    )}
                  >
                    <td className="px-5 py-3 font-medium text-ink">{location.name}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-muted">{location.city}</td>
                    <td className="px-5 py-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!location.is_active}
                        title="Изменить сотрудников"
                        onClick={() => setAssignment(location)}
                      >
                        <UsersRound className="size-3.5" />
                        {location.active_employee_count} активных · {location.total_employee_count}{' '}
                        всего
                      </Button>
                    </td>
                    <td className="px-5 py-3">
                      {location.is_active ? (
                        <Badge tone="positive">Активна</Badge>
                      ) : (
                        <Badge tone="neutral">В архиве</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {location.is_active ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Изменить"
                              onClick={() => setForm({ mode: 'edit', location })}
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Архивировать"
                              onClick={() => setArchiveTarget(location)}
                            >
                              <Archive className="size-3.5" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            title="Восстановить"
                            loading={restore.isPending && restore.variables === location.id}
                            onClick={() =>
                              restore.mutate(location.id, {
                                onSuccess: () => toast.success('Точка восстановлена'),
                                onError,
                              })
                            }
                          >
                            <RotateCcw className="size-3.5" />
                            Восстановить
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LocationFormDialog state={form} onClose={() => setForm(null)} />

      <EmployeeAssignmentDialog
        location={assignment}
        locations={locations.data ?? []}
        employees={employees.data ?? []}
        loading={employees.isLoading}
        error={employees.error}
        onRetry={() => void employees.refetch()}
        onClose={() => setAssignment(null)}
      />

      {archiveTarget && archiveTarget.assigned_user_count > 0 ? (
        <Modal
          open
          onOpenChange={(open) => !open && setArchiveTarget(null)}
          title={`Нельзя архивировать «${archiveTarget.name}»`}
          description={`К точке привязано учётных записей: ${archiveTarget.assigned_user_count}. Сначала перенесите или открепите их.`}
          footer={
            <>
              <Button onClick={() => setArchiveTarget(null)}>Закрыть</Button>
              <Button
                variant="primary"
                onClick={() => {
                  const locationId = archiveTarget.id
                  setArchiveTarget(null)
                  void navigate({
                    to: '/users',
                    search: { page: 1, location_id: locationId },
                  })
                }}
              >
                Открыть пользователей
              </Button>
            </>
          }
        />
      ) : (
        <ConfirmDialog
          open={archiveTarget !== null}
          onOpenChange={(open) => !open && setArchiveTarget(null)}
          title={`Архивировать «${archiveTarget?.name ?? ''}»?`}
          description="Точка исчезнет из новых назначений, но сохранится в истории разговоров. Её можно будет восстановить."
          confirmLabel="Архивировать"
          destructive
          loading={archive.isPending}
          onConfirm={() => {
            if (!archiveTarget) return
            archive.mutate(archiveTarget.id, {
              onSuccess: () => {
                toast.success('Точка перемещена в архив')
                setArchiveTarget(null)
              },
              onError,
            })
          }}
        />
      )}
    </div>
  )
}
